import { useInfiniteQuery } from '@tanstack/react-query'

interface QueryParams<TParams> {
  queryKey: string
  queryFn: (params: TParams) => Promise<any>
  perPage: number
  params: TParams
}

interface QueryResults<TData> {
  data: TData[]
  fetchNextPage: () => void
  hasNextPage: boolean | undefined
  isFetchingNextPage: boolean
  isLoading: boolean
  isError: boolean
  currentPage: number
  totalPages: number
}
export const useInfiniteQueryDataFetcher = <TParams, TData>({
  queryKey,
  queryFn,
  params,
  perPage
}: QueryParams<TParams>): QueryResults<TData> => {
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: [queryKey, params],
    queryFn: ({ pageParam = 1 }) =>
      queryFn({
        ...params,
        page: pageParam
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) return undefined
      const totalItems = lastPage.totalCount
      const totalPages = Math.ceil(totalItems / perPage)
      return allPages.length < totalPages ? allPages.length + 1 : undefined
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    retry: 3
  })

  const currentPage = data?.pages.length || 1
  const totalPages = Math.ceil((data?.pages?.[0].totalCount || 0) / perPage)
  const flatData = data?.pages.flatMap((page) => page?.data || []) || []
  return {
    data: flatData,
    isLoading,
    currentPage,
    totalPages,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  }
}
